import AppButton from '@nxsys/components/AppButton';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { Icons } from '@nxsys/types/icons';
import {
  defaultPayrollFreeMethod,
  invoiceMethodOptions,
  invoicePaymentModelOptions,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { ICompany } from '@nxsys/types/models/company';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import {
  defaultInvoiceRaisedAfterOptions,
  defaultInvoiceRaisedMethodOptions,
  defaultInvoiceTemplateOptions,
  defaultRuleInvoicingMethod,
} from '@nxsys/utils';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toNumber } from 'lodash';
import InvoiceSettingForm, {
  InvoiceSettingFormRef,
  InvoiceSettingValues,
} from './components/InvoiceSettingForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const InvoiceSetting: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<InvoiceSettingFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;

  const invoiceSetting = data?.invoiceSetting as any;

  const handleSubmit = useCallback(
    async (values: InvoiceSettingValues) => {
      try {
        setIsUpdating(true);

        let newInvoiceSetting = {};
        if (!isBureau) {
          newInvoiceSetting = {
            raiseInvoice: values.raiseInvoice || false,
            raiseInvoicePEO: values.raiseInvoicePEO || false,
            invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
            invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
            dueDate: toNumber(values.dueDate),
            rule: values.rule?.value || null,
            invoiceForSalary: values.invoiceForSalary,
            vatSalaryRate: values.vatSalaryRate || 0,
            vatSalaryRateCode: values.vatSalaryRateCode
              ? {
                  label: values.vatSalaryRateCode.label,
                  value: values.vatSalaryRateCode.value,
                  VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
                  VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceForSalaryDescription:
              values.invoiceForSalaryDescription ?? null,
            invoiceForERNI: values.invoiceForERNI ?? undefined,
            vatERNIRate: values.vatERNIRate || 0,
            vatERNIRateCode: values.vatERNIRateCode
              ? {
                  label: values.vatERNIRateCode.label,
                  value: values.vatERNIRateCode.value,
                  VATRate: values.vatERNIRateCode.fullData?.VATRate || '',
                  VATCode: values.vatERNIRateCode.fullData?.VATCode || '',
                }
              : null,
            erNIPercent: values.erNIPercent || 0,
            invoiceForERNIDescription: values.invoiceForERNIDescription ?? null,
            invoiceERPension: values.invoiceERPension,
            vatERPensionRate: values.vatERPensionRate || 0,
            vatERPensionRateCode: values.vatERPensionRateCode
              ? {
                  label: values.vatERPensionRateCode.label,
                  value: values.vatERPensionRateCode.value,
                  VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
                  VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceERPensionDescription:
              values.invoiceERPensionDescription ?? null,
            invoiceAppLevy: values.invoiceAppLevy,
            vatAppLevyRate: values.vatAppLevyRate || 0,
            vatAppLevyRateCode: values.vatAppLevyRateCode
              ? {
                  label: values.vatAppLevyRateCode.label,
                  value: values.vatAppLevyRateCode.value,
                  VATRate: values.vatAppLevyRateCode.fullData?.VATRate || '',
                  VATCode: values.vatAppLevyRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceAppLevyDescription: values.invoiceAppLevyDescription ?? null,
            invoicePayrollFee: values.invoicePayrollFee,
            vatPayrollFeeRate: values.vatPayrollFeeRate || 0,
            vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
              ? {
                  label: values.vatPayrollFeeRateCode.label,
                  value: values.vatPayrollFeeRateCode.value,
                  VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
                  VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
                }
              : null,
            invoicePayrollFeeDescription:
              values.invoicePayrollFeeDescription ?? null,
            invoicingMethod: (values.invoicingMethod as any)?.value || null,
            feePerEmployee: values.feePerEmployee,
            tieredFees: values.tieredFees ?? null,
            invoiceTemplate: values?.invoiceTemplate?.value || 'DEFAULT',
          };
        } else if (isUK) {
          newInvoiceSetting = {
            raiseInvoice: values.raiseInvoice || false,
            raiseInvoicePEO: values.raiseInvoicePEO || false,
            invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
            invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
            dueDate: toNumber(values.dueDate),
            rule: values.rule?.value || null,
            invoiceForSalary: values.invoiceForSalary,
            vatSalaryRate: values.vatSalaryRate || 0,
            vatSalaryRateCode: values.vatSalaryRateCode
              ? {
                  label: values.vatSalaryRateCode.label,
                  value: values.vatSalaryRateCode.value,
                  VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
                  VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceForSalaryDescription:
              values.invoiceForSalaryDescription ?? null,
            invoiceForERNI: values.invoiceForERNI ?? undefined,
            vatERNIRate: values.vatERNIRate || 0,
            vatERNIRateCode: values.vatERNIRateCode
              ? {
                  label: values.vatERNIRateCode.label,
                  value: values.vatERNIRateCode.value,
                  VATRate: values.vatERNIRateCode.fullData?.VATRate || '',
                  VATCode: values.vatERNIRateCode.fullData?.VATCode || '',
                }
              : null,
            erNIPercent: values.erNIPercent || 0,
            invoiceForERNIDescription: values.invoiceForERNIDescription ?? null,
            invoiceERPension: values.invoiceERPension,
            vatERPensionRate: values.vatERPensionRate || 0,
            vatERPensionRateCode: values.vatERPensionRateCode
              ? {
                  label: values.vatERPensionRateCode.label,
                  value: values.vatERPensionRateCode.value,
                  VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
                  VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceERPensionDescription:
              values.invoiceERPensionDescription ?? null,
            invoiceAppLevy: values.invoiceAppLevy,
            vatAppLevyRate: values.vatAppLevyRate || 0,
            vatAppLevyRateCode: values.vatAppLevyRateCode
              ? {
                  label: values.vatAppLevyRateCode.label,
                  value: values.vatAppLevyRateCode.value,
                  VATRate: values.vatAppLevyRateCode.fullData?.VATRate || '',
                  VATCode: values.vatAppLevyRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceAppLevyDescription: values.invoiceAppLevyDescription ?? null,
            invoicePayrollFee: values.invoicePayrollFee,
            vatPayrollFeeRate: values.vatPayrollFeeRate || 0,
            vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
              ? {
                  label: values.vatPayrollFeeRateCode.label,
                  value: values.vatPayrollFeeRateCode.value,
                  VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
                  VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
                }
              : null,
            invoicePayrollFeeDescription:
              values.invoicePayrollFeeDescription ?? null,
            invoicingMethod: (values.invoicingMethod as any)?.value || null,
            feePerEmployee: values.feePerEmployee,
            tieredFees: values.tieredFees ?? null,
            invoiceTemplate: values?.invoiceTemplate?.value || 'DEFAULT',
          };
        } else if (isIreland) {
          newInvoiceSetting = {
            raiseInvoice: values.raiseInvoice || false,
            raiseInvoicePEO: values.raiseInvoicePEO || false,
            invoicePaymentModel:
              values.invoicePaymentModel?.value === 'ALL'
                ? [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.PAYEE]
                : values.invoicePaymentModel?.value
                  ? [values.invoicePaymentModel?.value]
                  : [],
            invoiceMethod: values.invoiceMethod?.value || undefined,
            invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
            invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
            dueDate: toNumber(values.dueDate),
            rule: values.rule?.value || null,
            invoiceForSalary: values.invoiceForSalary,
            vatSalaryRate: values.vatSalaryRate || 0,
            vatSalaryRateCode: values.vatSalaryRateCode
              ? {
                  label: values.vatSalaryRateCode.label,
                  value: values.vatSalaryRateCode.value,
                  VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
                  VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceForSalaryDescription:
              values.invoiceForSalaryDescription ?? '',
            invoiceForEmployerPRSI: values.invoiceForEmployerPRSI ?? '',
            vatEmployerPRSIRate: values.vatEmployerPRSIRate || 0,
            vatEmployerPRSIRateCode: values.vatEmployerPRSIRateCode
              ? {
                  label: values.vatEmployerPRSIRateCode.label,
                  value: values.vatEmployerPRSIRateCode.value,
                  VATRate:
                    values.vatEmployerPRSIRateCode.fullData?.VATRate || '',
                  VATCode:
                    values.vatEmployerPRSIRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceForEmployerPRSIDescription:
              values.invoiceForEmployerPRSIDescription ?? null,
            invoiceERPension: values.invoiceERPension,
            vatERPensionRate: values.vatERPensionRate || 0,
            vatERPensionRateCode: values.vatERPensionRateCode
              ? {
                  label: values.vatERPensionRateCode.label,
                  value: values.vatERPensionRateCode.value,
                  VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
                  VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceERPensionDescription:
              values.invoiceERPensionDescription ?? null,
            invoiceForUSC: values.invoiceForUSC ?? '',
            vatUSCRate: values.vatUSCRate || 0,
            vatUSCRateCode: values.vatUSCRateCode
              ? {
                  label: values.vatUSCRateCode.label,
                  value: values.vatUSCRateCode.value,
                  VATRate: values.vatUSCRateCode.fullData?.VATRate || '',
                  VATCode: values.vatUSCRateCode.fullData?.VATCode || '',
                }
              : null,
            invoiceForUSCDescription: values.invoiceForUSCDescription ?? null,
            invoicePayrollFee: values.invoicePayrollFee,
            vatPayrollFeeRate: values.vatPayrollFeeRate || 0,
            vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
              ? {
                  label: values.vatPayrollFeeRateCode.label,
                  value: values.vatPayrollFeeRateCode.value,
                  VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
                  VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
                }
              : null,
            invoicePayrollFeeDescription:
              values.invoicePayrollFeeDescription ?? null,
            invoicingMethod: (values.invoicingMethod as any)?.value || null,
            feePerEmployee: values.feePerEmployee,
            tieredFees: values.tieredFees ?? null,
            invoiceTemplate: values?.invoiceTemplate?.value || 'DEFAULT',
          };
        }

        const params: UpdateCompanyByIdParams = {
          invoiceSetting: {
            ...data?.invoiceSetting,
            ...newInvoiceSetting,
          },
        } as any;

        await updateData(params);
      } catch (error) {
        console.error('Error updating invoice setting:', error);
      } finally {
        setIsUpdating(false);
      }
    },
    [data, updateData, isBureau, isUK, isIreland],
  );

  return (
    <div className="InvoiceSetting">
      {data && (
        <InvoiceSettingForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={
            {
              raiseInvoice: invoiceSetting?.raiseInvoice ?? null,
              raiseInvoicePEO: invoiceSetting?.raiseInvoicePEO ?? null,
              invoiceRaisedAfter:
                defaultInvoiceRaisedAfterOptions.find(
                  (item) => item.value === invoiceSetting?.invoiceRaisedAfter,
                ) || null,
              invoiceRaisedMethod:
                defaultInvoiceRaisedMethodOptions.find(
                  (item) => item.value === invoiceSetting?.invoiceRaisedMethod,
                ) || null,
              dueDate: invoiceSetting?.dueDate,
              rule:
                defaultRuleInvoicingMethod.find(
                  (item) => item.value === invoiceSetting?.rule,
                ) || null,
              invoiceForSalary: invoiceSetting?.invoiceForSalary ?? null,
              vatSalaryRate: invoiceSetting?.vatSalaryRate ?? null,
              vatSalaryRateCode: invoiceSetting?.vatSalaryRateCode
                ? {
                    label: invoiceSetting.vatSalaryRateCode.label,
                    value: invoiceSetting.vatSalaryRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatSalaryRateCode.VATRate,
                      VATCode: invoiceSetting.vatSalaryRateCode.VATCode,
                    },
                  }
                : null,
              invoiceForSalaryDescription:
                invoiceSetting?.invoiceForSalaryDescription ?? null,
              invoiceForERNI: invoiceSetting?.invoiceForERNI ?? null,
              erNIPercent: invoiceSetting?.erNIPercent ?? null,
              vatERNIRate: invoiceSetting?.vatERNIRate ?? null,
              vatERNIRateCode: invoiceSetting?.vatERNIRateCode
                ? {
                    label: invoiceSetting.vatERNIRateCode.label,
                    value: invoiceSetting.vatERNIRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatERNIRateCode.VATRate,
                      VATCode: invoiceSetting.vatERNIRateCode.VATCode,
                    },
                  }
                : null,
              invoiceForERNIDescription:
                invoiceSetting?.invoiceForERNIDescription ?? null,
              invoiceERPension: invoiceSetting?.invoiceERPension ?? null,
              vatERPensionRate: invoiceSetting?.vatERPensionRate ?? null,
              vatERPensionRateCode: invoiceSetting?.vatERPensionRateCode
                ? {
                    label: invoiceSetting.vatERPensionRateCode.label,
                    value: invoiceSetting.vatERPensionRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatERPensionRateCode.VATRate,
                      VATCode: invoiceSetting.vatERPensionRateCode.VATCode,
                    },
                  }
                : null,
              invoiceERPensionDescription:
                invoiceSetting?.invoiceERPensionDescription ?? null,
              invoiceAppLevy: invoiceSetting?.invoiceAppLevy ?? null,
              vatAppLevyRate: invoiceSetting?.vatAppLevyRate ?? null,
              vatAppLevyRateCode: invoiceSetting?.vatAppLevyRateCode
                ? {
                    label: invoiceSetting.vatAppLevyRateCode.label,
                    value: invoiceSetting.vatAppLevyRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatAppLevyRateCode.VATRate,
                      VATCode: invoiceSetting.vatAppLevyRateCode.VATCode,
                    },
                  }
                : null,
              invoiceAppLevyDescription:
                invoiceSetting?.invoiceAppLevyDescription ?? null,
              invoicePayrollFee: invoiceSetting?.invoicePayrollFee ?? null,
              vatPayrollFeeRate: invoiceSetting?.vatPayrollFeeRate ?? null,
              vatPayrollFeeRateCode: invoiceSetting?.vatPayrollFeeRateCode
                ? {
                    label: invoiceSetting.vatPayrollFeeRateCode.label,
                    value: invoiceSetting.vatPayrollFeeRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatPayrollFeeRateCode.VATRate,
                      VATCode: invoiceSetting.vatPayrollFeeRateCode.VATCode,
                    },
                  }
                : null,
              invoicePayrollFeeDescription:
                invoiceSetting?.invoicePayrollFeeDescription ?? null,
              invoicingMethod: invoiceSetting?.invoicingMethod
                ? (defaultPayrollFreeMethod.find(
                    (item) => item.value === invoiceSetting.invoicingMethod,
                  ) as any) || undefined
                : undefined,
              feePerEmployee: invoiceSetting?.feePerEmployee
                ? String(invoiceSetting.feePerEmployee)
                : undefined,
              tieredFees: invoiceSetting?.tieredFees ?? null,
              invoiceTemplate:
                defaultInvoiceTemplateOptions.find(
                  (item) => item.value === invoiceSetting?.invoiceTemplate,
                ) || 'DEFAULT',
              invoicePaymentModel:
                invoiceSetting?.invoicePaymentModel?.length === 2
                  ? invoicePaymentModelOptions?.find(
                      (item) => item.value === 'ALL',
                    )
                  : invoicePaymentModelOptions?.find(
                      (item) =>
                        item.value === invoiceSetting?.invoicePaymentModel?.[0],
                    ) || null,
              invoiceMethod:
                invoiceMethodOptions.find(
                  (item) => item.value === invoiceSetting?.invoiceMethod,
                ) || null,
              invoiceForEmployerPRSI:
                invoiceSetting?.invoiceForEmployerPRSI ?? null,
              vatEmployerPRSIRate: invoiceSetting?.vatEmployerPRSIRate ?? null,
              vatEmployerPRSIRateCode: invoiceSetting?.vatEmployerPRSIRateCode
                ? {
                    label: invoiceSetting.vatEmployerPRSIRateCode.label,
                    value: invoiceSetting.vatEmployerPRSIRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatEmployerPRSIRateCode.VATRate,
                      VATCode: invoiceSetting.vatEmployerPRSIRateCode.VATCode,
                    },
                  }
                : null,
              invoiceForEmployerPRSIDescription:
                invoiceSetting?.invoiceForEmployerPRSIDescription ?? null,
              invoiceForUSC: invoiceSetting?.invoiceForUSC ?? null,
              vatUSCRate: invoiceSetting?.vatUSCRate ?? null,
              vatUSCRateCode: invoiceSetting?.vatUSCRateCode
                ? {
                    label: invoiceSetting.vatUSCRateCode.label,
                    value: invoiceSetting.vatUSCRateCode.value,
                    fullData: {
                      VATRate: invoiceSetting.vatUSCRateCode.VATRate,
                      VATCode: invoiceSetting.vatUSCRateCode.VATCode,
                    },
                  }
                : null,
              invoiceForUSCDescription:
                invoiceSetting?.invoiceForUSCDescription ?? null,
            } as any
          }
        />
      )}
      <div className="flex items-center justify-start gap-3 px-[16px] pb-[16px]">
        <AppButton
          type="primary"
          name={messages['common.save']}
          leftIcon={
            <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
          }
          onClick={() => {
            formRef.current?.submit();
          }}
          loading={isUpdating}
          disabled={isUpdating}
        />
      </div>
    </div>
  );
};

export default InvoiceSetting;
